import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  DollarSign,
  Bell,
  UserCircle,
  BarChart3,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  FileSpreadsheet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_520095f0-9852-4a0a-9b72-bc14f91bb550/artifacts/vzlnpfvd_Brillen%20only.png";

const navigationItems = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { name: "Leads", path: "/leads", icon: Users },
  { name: "Projects", path: "/projects", icon: FolderKanban },
  { name: "Finance", path: "/finance", icon: DollarSign },
  { name: "Tasks", path: "/tasks", icon: Bell },
  { name: "Team & HR", path: "/team", icon: UserCircle },
  { name: "Analytics", path: "/analytics", icon: BarChart3 },
  { name: "Settings", path: "/settings", icon: Settings },
  { name: "Quotation Generator", path: "/quotation-generator", icon: FileSpreadsheet },
];

export default function Sidebar() {
  const location = useLocation();
  const { user, logout, hasModuleAccess, token } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = useTheme();
  const [moduleUpdates, setModuleUpdates] = useState({});
  const [notificationCounts, setNotificationCounts] = useState({});

  useEffect(() => {
    if (token) {
      fetchNotificationCounts();
      // Refresh every 30 seconds
      const interval = setInterval(fetchNotificationCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  useEffect(() => {
    if (user?.role === "Admin") {
      fetchModuleUpdates();
      const interval = setInterval(fetchModuleUpdates, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchNotificationCounts = async () => {
    try {
      const response = await axios.get(`${API}/notifications/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationCounts(response.data.by_module || {});
    } catch (error) {
      console.error("Failed to fetch notification counts");
    }
  };

  const fetchModuleUpdates = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModuleUpdates(response.data.module_updates || {});
    } catch (error) {
      console.error("Failed to fetch module updates");
    }
  };

  const handleNavClick = async (moduleName) => {
    // Mark notifications as read when clicking on a module
    const moduleKey = moduleName.toLowerCase().replace(/\s+/g, '-');
    if (notificationCounts[moduleKey] > 0) {
      try {
        await axios.put(`${API}/notifications/module/${moduleKey}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update local state
        setNotificationCounts(prev => ({...prev, [moduleKey]: 0}));
      } catch (error) {
        console.error("Failed to mark notifications as read");
      }
    }
  };

  const filteredNavigationItems = user?.role === "Admin" 
    ? navigationItems 
    : navigationItems.filter(item => {
        const moduleName = item.path.substring(1);
        return hasModuleAccess(moduleName);
      });

  const getModuleUpdateCount = (path) => {
    const moduleName = path.substring(1);
    // Combine notification counts with module updates for admins
    const notifCount = notificationCounts[moduleName] || 0;
    const updateCount = moduleUpdates[moduleName] || 0;
    return notifCount > 0 ? notifCount : updateCount;
  };

  return (
    <div 
      className={cn(
        "h-screen bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 flex flex-col fixed left-0 top-0 transition-all duration-300 z-50",
        sidebarCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Brillien" 
                className="h-8 w-auto"
              />
            </div>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleSidebar}
            className="h-8 w-8 p-0 text-slate-500 hover:text-brillien-600 dark:text-slate-400 dark:hover:text-brillien-400"
            data-testid="toggle-sidebar-btn"
          >
            {sidebarCollapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </Button>
        </div>
        {!sidebarCollapsed && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 ml-1">CRM</p>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          {filteredNavigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                           (item.path === "/projects" && location.pathname.startsWith("/projects/")) ||
                           (item.path === "/quotation-generator" && location.pathname.startsWith("/quotation-generator/"));
            const updateCount = getModuleUpdateCount(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => handleNavClick(item.path.substring(1))}
                data-testid={`sidebar-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                title={sidebarCollapsed ? item.name : ""}
                className={cn(
                  "flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200",
                  sidebarCollapsed ? "justify-center px-2 py-2.5" : "justify-between px-3 py-2.5",
                  isActive
                    ? "bg-brillien-50 dark:bg-brillien-900/30 text-brillien-700 dark:text-brillien-400 border-l-4 border-brillien-600"
                    : "text-slate-600 dark:text-slate-300 hover:text-brillien-700 dark:hover:text-brillien-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                )}
              >
                <div className={cn("flex items-center gap-3", sidebarCollapsed && "justify-center")}>
                  <Icon 
                    className={cn(
                      "h-5 w-5",
                      isActive ? "text-brillien-600 dark:text-brillien-400" : "text-slate-400 dark:text-slate-500"
                    )} 
                    strokeWidth={1.5} 
                  />
                  {!sidebarCollapsed && item.name}
                </div>
                {!sidebarCollapsed && updateCount > 0 && (
                  <Badge className="bg-brillien-600 text-white border-0 h-5 min-w-5 flex items-center justify-center px-1.5 text-xs font-bold rounded-full animate-pulse">
                    {updateCount}
                  </Badge>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* User Profile & Logout */}
      <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        {/* User Info */}
        {!sidebarCollapsed && (
          <div className="mb-2 px-1">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{user?.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{user?.role}</p>
          </div>
        )}
        
        {/* Logout */}
        <Button
          onClick={logout}
          variant="ghost"
          size="sm"
          data-testid="logout-button"
          title={sidebarCollapsed ? "Logout" : ""}
          className={cn(
            "text-slate-600 dark:text-slate-300 hover:text-brillien-700 dark:hover:text-brillien-400 hover:bg-brillien-50 dark:hover:bg-brillien-900/30 rounded-lg",
            sidebarCollapsed ? "w-full justify-center" : "w-full justify-start"
          )}
        >
          <LogOut className="h-4 w-4" />
          {!sidebarCollapsed && <span className="ml-2">Logout</span>}
        </Button>
      </div>
    </div>
  );
}
